import React from 'react';
import EEPlaceholder from '@/components/Sections/EEPlaceholder';
import {
  GetStaticComponentProps,
  NextImage,
  useComponentProps,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { TeamMember, teamMemberService } from 'lib/services/TeamMemberService';
import { TeamMemberBanner } from './TeamMemberBanner';
import { TeamMemberContactLinks } from './TeamMemberContactLinks';
import { defaultLanguage } from 'temp/config';
import ScRichText from 'components/Generic/SitecoreWrappers/RichText';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Props = ComponentProps & {
  anchorId?: string;
};

const TeamMemberBiography: React.FC<Props> = ({ anchorId, rendering }) => {
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const teamMemberData = useComponentProps<TeamMember>(rendering.uid);
  if (!teamMemberData) return null;

  return isExperienceEditor ? (
    <EEPlaceholder>Team Member Biography</EEPlaceholder>
  ) : (
    <div id={anchorId}>
      <TeamMemberBanner teamMemberData={teamMemberData} />
      <section className="default-section-padding background-white">
        <div className="container">
          <div className="row gy-4">
            <div className="col-12 col-md-6">
              <div className="d-flex flex-column gap-4">
                {(teamMemberData.bio.length ?? 0) > 0 &&
                  teamMemberData.bio.map((item) => (
                    <ScRichText
                      key={item.description?.id}
                      fields={item}
                      spacingType="tight"
                      className="body-default text-grey"
                      rendering={rendering}
                    />
                  ))}
                <TeamMemberContactLinks teamMemberData={teamMemberData} />
              </div>
            </div>
            {teamMemberData.image?.src && (
              <div className="col-12 col-md-6 position-relative">
                <NextImage
                  field={teamMemberData.image}
                  alt="Profile picture"
                  width={576}
                  height={324}
                  sizes="
                    (max-width: 575px) 100vw,
                    (max-width: 767px) 510px,
                    (max-width: 991px) 330px,
                    (max-width: 1199px) 50vw,
                    570px
                  "
                  style={{
                    objectFit: 'contain',
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData, context) => {
  const language = layoutData.sitecore.context.language;
  const path = context.params?.path as string[];
  const slug = path?.at(-1);
  if (!slug) return { notFound: true };
  const teamMember = await teamMemberService.getTeamMember(slug, language ?? defaultLanguage);
  return teamMember ?? { notFound: true };
};

export default TeamMemberBiography;
